import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
} from '@mui/icons-material';
import {
  usePermissions,
  useSecurityUsers,
  useAssignPermissions,
  useToggleUserActive,
} from '../hooks/usePermissions';
import { UserPermissions as UserPermissionsType, Permission } from '../types';

const UserPermissionsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserPermissionsType | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

  const {
    data: permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
    isFetching: isStatsFetching,
  } = usePermissions();
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
    refetch,
    isFetching: isUsersFetching,
  } = useSecurityUsers();

  const assignMutation = useAssignPermissions();
  const toggleActiveMutation = useToggleUserActive();

  const isLoading = permissionsLoading || usersLoading;
  const error = permissionsError || usersError;

  // Filter users based on search
  const filteredUsers = users?.filter?.(
    (userPerm) =>
      userPerm.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userPerm.user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userPerm.user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get user's permission IDs
  const getUserPermissionIds = (userPerms: UserPermissionsType): number[] => {
    return userPerms.permissions.map((perm) => perm.id);
  };

  // Handle edit dialog open
  const handleEditOpen = (userPerms: UserPermissionsType) => {
    setSelectedUser(userPerms);
    setSelectedPermissions(getUserPermissionIds(userPerms));
    setEditDialog(true);
  };

  // Handle edit dialog close
  const handleEditClose = () => {
    setEditDialog(false);
    setSelectedUser(null);
    setSelectedPermissions([]);
  };

  // Handle permission toggle
  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Handle resource toggle (all permissions in a resource)
  const handleResourceToggle = (resource: Permission) => {
    const resourcePermissionIds = resource.permissions.map((p) => p.id);
    const allSelected = resourcePermissionIds.every((id) => selectedPermissions.includes(id));

    if (allSelected) {
      // Remove all permissions from this resource
      setSelectedPermissions((prev) => prev.filter((id) => !resourcePermissionIds.includes(id)));
    } else {
      // Add all permissions from this resource
      setSelectedPermissions((prev) => [
        ...prev.filter((id) => !resourcePermissionIds.includes(id)),
        ...resourcePermissionIds,
      ]);
    }
  };

  // Handle save permissions
  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      await assignMutation.mutateAsync({
        userId: selectedUser.user.id,
        permissionIds: selectedPermissions,
      });
      handleEditClose();
    } catch (error) {
      console.error('Failed to update permissions:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 3,
            }}
          >
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'transparent !important',
                border: 'none',
                color: '#ffffff',
                padding: 0,
                '& .MuiAlert-icon': { color: '#f44336' },
                '& .MuiAlert-message': { color: '#ffffff', padding: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#ffffff !important' }}>
                Failed to Load User Permissions
              </Typography>
              <Typography
                variant="body2"
                paragraph
                sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}
              >
                {error instanceof Error
                  ? error.message
                  : 'An error occurred while fetching permissions. Please try again.'}
              </Typography>
            </Alert>
          </Paper>
          <Button
            variant="contained"
            onClick={() => refetch()}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)' },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {(isStatsFetching || isUsersFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}
      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 8px 24px rgba(255, 152, 0, 0.3)',
              },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Total Users
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#ff9800' }} mt={0.5}>
              {users?.length || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 8px 24px rgba(76, 175, 80, 0.3)',
              },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Total Permissions
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#66bb6a' }} mt={0.5}>
              {permissions?.reduce((sum, p) => sum + p.permissions.length, 0) || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 8px 24px rgba(33, 150, 243, 0.3)',
              },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Resources
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#42a5f5' }} mt={0.5}>
              {permissions?.length || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
              border: '1px solid rgba(186, 104, 200, 0.3)',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 8px 24px rgba(186, 104, 200, 0.3)',
              },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Avg Permissions/User
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#ba68c8' }} mt={0.5}>
              {users?.length
                ? Math.round(users.reduce((sum, u) => sum + u.permission_count, 0) / users.length)
                : 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          borderRadius: 3,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search by name, username, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#ff9800' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': { borderColor: 'rgba(255, 152, 0, 0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 152, 0, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#ff9800' },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          }}
        />
      </Paper>

      {/* Users List */}
      <Grid container spacing={3}>
        {filteredUsers?.map((userPerm) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={userPerm.user.id}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
                borderRadius: 3,
                border: '1px solid rgba(255, 152, 0, 0.3)',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(255, 152, 0, 0.3)',
                  transform: 'translateY(-4px)',
                  borderColor: 'rgba(255, 152, 0, 0.5)',
                },
              }}
            >
              {/* User Header */}
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: '#ff9800',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#ffffff',
                    }}
                  >
                    {userPerm.user.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff' }}>
                      {userPerm.user.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      @{userPerm.user.username}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Tooltip title={userPerm.user.is_active ? 'Disable account' : 'Enable account'}>
                    <IconButton
                      size="small"
                      onClick={() => toggleActiveMutation.mutate(userPerm.user.id)}
                      disabled={toggleActiveMutation.isPending}
                      sx={{
                        bgcolor: userPerm.user.is_active
                          ? 'rgba(244, 67, 54, 0.12)'
                          : 'rgba(76, 175, 80, 0.12)',
                        color: userPerm.user.is_active ? '#f44336' : '#4caf50',
                        '&:hover': {
                          bgcolor: userPerm.user.is_active
                            ? 'rgba(244, 67, 54, 0.22)'
                            : 'rgba(76, 175, 80, 0.22)',
                        },
                      }}
                    >
                      {userPerm.user.is_active ? (
                        <BlockIcon fontSize="small" />
                      ) : (
                        <EnableIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Permissions">
                    <IconButton
                      size="small"
                      onClick={() => handleEditOpen(userPerm)}
                      sx={{
                        bgcolor: 'rgba(255, 152, 0, 0.15)',
                        color: '#ff9800',
                        '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.25)' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Role + status badges */}
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Chip
                  label={userPerm.user.role}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    bgcolor: 'rgba(255, 152, 0, 0.2)',
                    color: '#ff9800',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                  }}
                />
                {!userPerm.user.is_active && (
                  <Chip
                    label="Disabled"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'rgba(244, 67, 54, 0.15)',
                      color: '#f44336',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                    }}
                  />
                )}
              </Box>

              {/* Permissions Count */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 152, 0, 0.1)',
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  Total Permissions
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#ff9800' }}>
                  {userPerm.permission_count}
                </Typography>
              </Box>

              {/* Permissions by Resource */}
              <Box mt={2}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  mb={1}
                  display="block"
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  Permissions by Resource
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {Array.from(
                    userPerm.permissions.reduce((map, perm) => {
                      const current = map.get(perm.resource) || 0;
                      map.set(perm.resource, current + 1);
                      return map;
                    }, new Map<string, number>())
                  ).map(([resource, count]) => (
                    <Chip
                      key={resource}
                      label={`${resource} (${count})`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: '24px',
                        bgcolor: 'rgba(33, 150, 243, 0.15)',
                        color: '#42a5f5',
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                        textTransform: 'capitalize',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Edit Permissions Dialog */}
      <Dialog
        open={editDialog}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: 4, border: '1px solid rgba(255, 152, 0, 0.3)' } },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
            borderBottom: '1px solid rgba(255, 152, 0, 0.3)',
            pb: 2.5,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  bgcolor: '#ff9800',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SecurityIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#ff9800' }}>
                  Edit Permissions
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {selectedUser?.user.name} (@{selectedUser?.user.username})
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleEditClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} mb={3}>
            Select permissions for this user. Permissions are grouped by resource.
          </Typography>

          {permissions?.map((resource) => {
            const resourcePermissionIds = resource.permissions.map((p) => p.id);
            const allSelected = resourcePermissionIds.every((id) =>
              selectedPermissions.includes(id)
            );
            const someSelected =
              resourcePermissionIds.some((id) => selectedPermissions.includes(id)) && !allSelected;

            return (
              <Accordion
                key={resource.resource}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 152, 0, 0.2)',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                  sx={{
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => handleResourceToggle(resource)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#ffffff' }}>
                        {resource.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {resource.permissions.length} permissions
                      </Typography>
                    </Box>
                    <Chip
                      label={`${
                        resource.permissions.filter((p) => selectedPermissions.includes(p.id))
                          .length
                      }/${resource.permissions.length}`}
                      size="small"
                      color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Grid container spacing={1}>
                    {resource.permissions.map((perm) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={perm.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => handlePermissionToggle(perm.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: '#ffffff' }}
                              >
                                {perm.action}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                              >
                                {perm.description}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            width: '100%',
                            m: 0,
                            p: 1,
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid rgba(255, 152, 0, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.05)',
          }}
        >
          <Button
            onClick={handleEditClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 152, 0, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 152, 0, 0.5)',
                bgcolor: 'rgba(255, 152, 0, 0.08)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSavePermissions}
            disabled={assignMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)' },
              '&:disabled': { background: 'rgba(255, 152, 0, 0.3)' },
            }}
          >
            {assignMutation.isPending ? 'Saving...' : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPermissionsPage;
